import React, { useEffect, useMemo, useRef } from "react";

import {

  aspectFromSize,

  computeFittedStageSize,

  TOOL_PREP_STAGE_INSET,

  useImageNaturalSize,

  useToolPrepPreviewViewport,

} from "../../../lib/toolPrepImageLayout";

import RoundLoader from "../../../../../components/ui/RoundLoader";

import CircleCropOverlay from "./CircleCropOverlay";

import SoftWatermarkOverlay from "../../../shared/Popup/SoftWatermarkOverlay";

import { getCircleCropSourceRect } from "../../../lib/circleCropPrepModel";

import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";

import "./CircleCropPreview.css";



const EMPTY_STAGE_ASPECT = 1;



function fitContainSize(srcW, srcH, maxW, maxH) {

  const w = Math.max(1, Number(srcW) || 0);

  const h = Math.max(1, Number(srcH) || 0);

  if (!w || !h) {

    return { width: maxW, height: maxH };

  }

  const scale = Math.min(maxW / w, maxH / h, 1);

  return {

    width: Math.max(24, Math.round(w * scale)),

    height: Math.max(24, Math.round(h * scale)),

  };

}



const CircleCropPreview = ({

  imageUrl = null,

  settings = null,

  loading = false,

  applying = false,

  resultMode = false,

  resultImageUrl = null,

  emptyLabel = "Upload an image to preview",

  onFramesChange = () => {},

  onHostMetricsChange = null,

  disabled = false,

  softWatermarkToolKey = "circle_crop",

}) => {

  const stageRef = useRef(null);

  const naturalSize = useImageNaturalSize(imageUrl);

  const resultNaturalSize = useImageNaturalSize(resultMode ? resultImageUrl : null);

  const viewport = useToolPrepPreviewViewport(stageRef);



  const stageSize = useMemo(() => {

    const vw = viewport.width;

    const vh = viewport.height;

    if (!vw || !vh) {

      return !imageUrl && !resultImageUrl

        ? { width: 420, height: 420 }

        : { width: 0, height: 0 };

    }



    if (!imageUrl && !resultImageUrl) {

      return (

        computeFittedStageSize(vw, vh, EMPTY_STAGE_ASPECT, {

          inset: TOOL_PREP_STAGE_INSET,

        }) ?? { width: 420, height: 420 }

      );

    }



    if (resultMode && resultImageUrl) {

      const aspect = aspectFromSize(

        resultNaturalSize.width,

        resultNaturalSize.height,

        EMPTY_STAGE_ASPECT

      );

      return (

        computeFittedStageSize(vw, vh, aspect, {

          inset: TOOL_PREP_STAGE_INSET,

        }) ?? { width: 0, height: 0 }

      );

    }



    return (

      computeFittedStageSize(vw, vh, EMPTY_STAGE_ASPECT, {

        inset: TOOL_PREP_STAGE_INSET,

      }) ?? { width: 0, height: 0 }

    );

  }, [

    imageUrl,

    resultImageUrl,

    resultMode,

    resultNaturalSize.height,

    resultNaturalSize.width,

    viewport.height,

    viewport.width,

  ]);



  const squareSize = useMemo(() => {

    if (!stageSize.width || !stageSize.height) {

      return { width: 0, height: 0 };

    }

    if (resultMode && resultImageUrl) {

      return fitContainSize(

        resultNaturalSize.width || stageSize.width,

        resultNaturalSize.height || stageSize.height,

        stageSize.width,

        stageSize.height

      );

    }

    const side = Math.min(stageSize.width, stageSize.height);

    return { width: side, height: side };

  }, [

    resultImageUrl,

    resultMode,

    resultNaturalSize.height,

    resultNaturalSize.width,

    stageSize.height,

    stageSize.width,

  ]);



  useEffect(() => {

    if (resultMode || applying) return;

    if (!onHostMetricsChange || !squareSize.width || !naturalSize.width) return;

    onHostMetricsChange({

      hostWidth: squareSize.width,

      hostHeight: squareSize.height,

      naturalWidth: naturalSize.width,

      naturalHeight: naturalSize.height,

    });

  }, [

    applying,

    naturalSize.height,

    naturalSize.width,

    onHostMetricsChange,

    resultMode,

    squareSize.height,

    squareSize.width,

  ]);



  const showEditOverlay = !resultMode && imageUrl && squareSize.width;

  const showResult = resultMode && resultImageUrl && squareSize.width;



  return (

    <ToolPrepCanvasStage

      className="circle-crop-preview"

      ref={stageRef}

      metaImageUrl={imageUrl}

      metaWidth={

        Math.round(

          (getCircleCropSourceRect(settings)?.w || 1) * (naturalSize.width || 0)

        ) || naturalSize.width

      }

      metaHeight={

        Math.round(

          (getCircleCropSourceRect(settings)?.h || 1) * (naturalSize.height || 0)

        ) || naturalSize.height

      }

    >

      <div

        className={`circle-crop-preview__stage${

          resultMode ? " circle-crop-preview__stage--result" : ""

        }`}

        style={{

          width: squareSize.width || stageSize.width || undefined,

          height: squareSize.height || stageSize.height || undefined,

        }}

      >

        {loading ? (

          <div className="circle-crop-preview__loading">

            <RoundLoader size="md" label="Removing background…" />

          </div>

        ) : applying ? (

          <div className="circle-crop-preview__loading">

            <RoundLoader size="md" label="Applying crop…" />

          </div>

        ) : showResult ? (

          <>

            <img

              src={resultImageUrl}

              alt="Circle cropped result"

              className="circle-crop-preview__result"

              style={{

                width: squareSize.width,

                height: squareSize.height,

              }}

              draggable={false}

            />

            <SoftWatermarkOverlay toolKey={softWatermarkToolKey} />

          </>

        ) : showEditOverlay ? (

          <CircleCropOverlay

            imageUrl={imageUrl}

            settings={settings}

            width={squareSize.width}

            height={squareSize.height}

            onFramesChange={onFramesChange}

            disabled={disabled}

          />

        ) : (

          <div className="circle-crop-preview__empty">{emptyLabel}</div>

        )}

      </div>


    </ToolPrepCanvasStage>

  );

};



export default CircleCropPreview;

